import { Link } from "react-router-dom";
import { Check, X, Copy, ExternalLink, ArrowRight } from "lucide-react";
import { useState } from "react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { DISCOUNT_CODE, DISCOUNT_PERCENT, PARTNER_LINK } from "@/lib/constants";


const CANONICAL = "https://heilys.info/heilys-cashback";

const FAQ: { q: string; a: string }[] = [
  {
    q: "Gibt es ein heilys Cashback-Programm?",
    a: "Nach aktuellem Stand bietet heilys kein klassisches Cashback-Programm und ist auf keiner der großen Cashback-Plattformen wie Shoop oder iGraal gelistet. Statt einer verzögerten Rückerstattung erhältst du den Rabatt direkt im Checkout über den Code LONGEVITY (5 % auf das gesamte Sortiment).",
  },
  {
    q: "Was ist besser: Cashback oder Discount-Code?",
    a: `Für heilys ist der Discount-Code in der Praxis die bessere Wahl: ${DISCOUNT_PERCENT} % werden sofort vom Bestellwert abgezogen, ohne Account bei einem Cashback-Portal, ohne Tracking-Cookies und ohne Wartezeit auf die Auszahlung. Klassische Cashback-Quoten für Health-Tech-Geräte liegen meist bei 1–3 %.`,
  },
  {
    q: "Funktioniert LONGEVITY zusammen mit Cashback-Plattformen?",
    a: "Da heilys aktuell auf keiner Cashback-Plattform gelistet ist, stellt sich die Kombi-Frage praktisch nicht. Solltest du heilys künftig auf einem Portal finden, gilt in der Regel: ein Rabattcode plus Cashback ist nur möglich, wenn das Portal das explizit erlaubt. Wir aktualisieren diese Seite, sobald sich daran etwas ändert.",
  },
  {
    q: "Wie hoch ist die Ersparnis mit LONGEVITY genau?",
    a: `Der Code ${DISCOUNT_CODE} gibt ${DISCOUNT_PERCENT} % Rabatt auf den Bestellwert im Checkout. Beim heilys Gen30+ Vision (Straßenpreis ~159 €) sind das rund 8 €, bei größeren Panels entsprechend mehr. Den finalen Preis siehst du direkt im heilys Shop, sobald du den Code eingegeben hast.`,
  },
];

const COMPARISON_ROWS = [
  { label: "Ersparnis", cashback: "1–3 % typisch", code: `${DISCOUNT_PERCENT} % sofort` },
  { label: "Auszahlung", cashback: "Wochen verzögert", code: "Sofort im Checkout" },
  { label: "Account nötig", cashback: "Ja", code: "Nein" },
  { label: "Tracking-Cookies", cashback: "Erforderlich", code: "Nicht nötig" },
  { label: "Mindestbestellwert", cashback: "Häufig", code: "Keiner" },
  { label: "Bei heilys verfügbar", cashback: "Nein (aktuell)", code: "Ja" },
];

const breadcrumbStructuredData = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://heilys.info/" },
    { "@type": "ListItem", position: 2, name: "heilys Cashback", item: CANONICAL },
  ],
};

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const HeilysCashback = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(DISCOUNT_CODE).then(() => {
      setCopied(true);
      window.lh?.trackCodeCopy({ source: "cashback_page" });
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleAffiliate = () => window.lh?.trackAffiliate({ source: "cashback_page" });

  return (
    <Layout>
      <SEOHead
        title="heilys Cashback 2026: Discount LONGEVITY statt Gutscheinportal"
        description="Kein heilys Cashback-Portal nötig: Rabattcode LONGEVITY gibt 5 % sofort im Checkout – schneller als jedes Cashback. Vergleich, Erfahrungen, Trustpilot 4,3/5."
        canonical={CANONICAL}
        ogImage="https://heilys.info/images/hero-main.webp"
        ogImageAlt="heilys Cashback Alternative – Rabattcode LONGEVITY für 5 % Rotlichttherapie-Discount"
        schemaJson={[breadcrumbStructuredData, faqStructuredData]}
      />

      <section className="pt-10 pb-12 md:pt-16 md:pb-20">
        <div className="container max-w-3xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Start</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">heilys Cashback</span>
          </nav>
          <h1 className="font-display text-2xl font-bold text-foreground sm:text-3xl md:text-4xl">
            heilys Cashback: Discount-Code LONGEVITY als bessere Alternative
          </h1>
          <p className="mt-4 text-sm text-muted-foreground md:text-base">
            Du suchst nach einem heilys Cashback-Programm oder einem Rotlichttherapie-Discount?
            Nach aktuellem Stand bietet heilys kein klassisches Cashback an und ist auf keiner
            der großen Cashback-Plattformen gelistet. Stattdessen gibt es den Rabattcode{" "}
            <strong className="text-foreground">{DISCOUNT_CODE}</strong>: {DISCOUNT_PERCENT} %
            Rabatt direkt im Checkout des heilys Shops – ohne Account, ohne Wartezeit, ohne
            Tracking-Cookies. In der Praxis ist das schneller und meist auch lukrativer als die
            1–3 %, die klassische Cashback-Portale für Health-Tech-Geräte ausschütten.
          </p>

          <div className="mt-6 flex flex-col gap-3 rounded-xl border border-primary/40 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Aktueller Code</p>
              <p className="font-display text-xl font-bold text-foreground sm:text-2xl">{DISCOUNT_CODE}</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                onClick={handleCopy}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
              >
                {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                {copied ? "Kopiert" : "Code kopieren"}
              </button>
              <a
                href={PARTNER_LINK}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={handleAffiliate}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:brightness-110"
              >
                Zum heilys Shop
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-border/50 bg-card/30 py-10 md:py-14">
        <div className="container max-w-3xl">
          <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
            Cashback-Portal vs. Discount-Code im direkten Vergleich
          </h2>
          <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card">
            <div className="grid grid-cols-3 border-b border-border bg-secondary/40 text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:text-sm">
              <div className="p-3 sm:p-4">Merkmal</div>
              <div className="p-3 sm:p-4 text-center">Cashback-Portal</div>
              <div className="p-3 sm:p-4 text-center text-primary">Code {DISCOUNT_CODE}</div>
            </div>
            {COMPARISON_ROWS.map((row) => (
              <div key={row.label} className="grid grid-cols-3 border-b border-border/50 text-xs sm:text-sm last:border-b-0">
                <div className="p-3 sm:p-4 font-medium text-foreground">{row.label}</div>
                <div className="p-3 sm:p-4 text-center text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                    {row.cashback}
                  </span>
                </div>
                <div className="p-3 sm:p-4 text-center text-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-success" aria-hidden="true" />
                    {row.code}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container max-w-3xl">
          <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
            So holst du dir die {DISCOUNT_PERCENT} % – in 3 Schritten
          </h2>
          <ol className="mt-6 space-y-4">
            {[
              { n: 1, t: "Code kopieren", d: `Klicke oben auf "Code kopieren" – ${DISCOUNT_CODE} liegt direkt in deiner Zwischenablage.` },
              { n: 2, t: "heilys Shop öffnen", d: "Über den Button gelangst du in den offiziellen heilys Shop. Lege deine Wunschgeräte in den Warenkorb." },
              { n: 3, t: "Im Checkout einlösen", d: `Im Checkout-Schritt erscheint ein Feld für Rabattcodes. Füge ${DISCOUNT_CODE} ein – ${DISCOUNT_PERCENT} % werden sofort abgezogen.` },
            ].map((s) => (
              <li key={s.n} className="flex gap-4 rounded-lg border border-border bg-card p-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary font-display text-sm font-bold text-primary-foreground">
                  {s.n}
                </span>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground">{s.t}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-8 text-center">
            <a
              href={PARTNER_LINK}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={handleAffiliate}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:brightness-110"
            >
              Jetzt {DISCOUNT_PERCENT} % mit {DISCOUNT_CODE} sichern
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="border-t border-border/50 bg-card/30 py-10 md:py-14">
        <div className="container max-w-3xl">
          <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
            Was, wenn heilys später ein Cashback-Programm einführt?
          </h2>
          <p className="mt-4 text-sm text-muted-foreground md:text-base">
            Sollte heilys künftig auf Plattformen wie Shoop, iGraal oder Questler aktiv werden,
            aktualisieren wir diese Seite umgehend mit dem Vergleich der Konditionen. Bis dahin
            ist {DISCOUNT_CODE} die einzige offiziell funktionierende Spar-Option – und in den
            allermeisten Fällen ohnehin die finanziell bessere Wahl, da Health-Tech-Cashback
            selten über 3 % hinausgeht.
          </p>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container max-w-3xl">
          <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
            Häufige Fragen zum heilys Cashback
          </h2>
          <div className="mt-6 space-y-4">
            {FAQ.map((f) => (
              <details key={f.q} className="group rounded-lg border border-border bg-card p-4">
                <summary className="cursor-pointer font-display text-sm font-semibold text-foreground sm:text-base">
                  {f.q}
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border/50 bg-card/30 py-10 md:py-14">
        <div className="container max-w-3xl text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Vertrauen & Quellen</p>
          <p className="mt-3 text-sm text-muted-foreground md:text-base">
            heilys ist eine Marke der weCare+ GmbH aus Bielefeld und auf Trustpilot mit
            <strong className="text-foreground"> 4,3 / 5</strong> bewertet. heilys.info ist eine
            unabhängige Informationsseite und kein offizieller Shop – bei Käufen über unsere
            Partnerlinks erhalten wir eine Provision.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/" className="inline-flex items-center gap-1 text-foreground hover:text-primary">
              <ArrowRight className="h-3.5 w-3.5" /> Zur heilys Übersicht
            </Link>
            <Link to="/gen30-vision" className="inline-flex items-center gap-1 text-foreground hover:text-primary">
              <ArrowRight className="h-3.5 w-3.5" /> heilys Gen30+ Vision
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HeilysCashback;
